// @ts-nocheck
/**
 * <geolocation> — shared theme-level IP → ZIP auto-detection.
 *
 * Detects the visitor's ZIP once (via ipgeolocation.io), caches it in
 * localStorage, and reuses it across page loads and navigation so the API limit
 * is not burned on every view. Consumers (delivery widgets on the PDP, and
 * later the PLP / other pages) await detectZip() and read getGeoZip(). The
 * `geo-zip` document event is kept as a public hook for consumers that cannot
 * await the promise; nothing in the theme listens to it today. The customer can
 * always override the detected value — a manual correction is stored as a
 * user-set ZIP that detection never overwrites.
 */

const ZIP_KEY = 'geo_zip';
const SRC_KEY = 'geo_zip_source'; // 'auto' | 'user'
const TS_KEY = 'geo_zip_ts';
const ATTEMPT_KEY = 'geo_attempted'; // sessionStorage — one API attempt per visit
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // auto-detected value re-checked at most weekly
const TIMEOUT_MS = 6000; // a hanging API must not hold consumers' fallback ZIP hostage

const ENDPOINT = 'https://api.ipgeolocation.io/ipgeo';

let inFlight = null;

const readLS = (key) => {
  try { return localStorage.getItem(key); } catch (e) { return null; }
};
const writeLS = (key, value) => {
  try { localStorage.setItem(key, value); } catch (e) { /* storage unavailable */ }
};
const zip5 = (value) => String(value || '').replace(/\D/g, '').slice(0, 5);
const wasAttempted = () => {
  try { return sessionStorage.getItem(ATTEMPT_KEY) === '1'; } catch (e) { return false; }
};
const markAttempted = () => {
  try { sessionStorage.setItem(ATTEMPT_KEY, '1'); } catch (e) { /* no-op */ }
};

/** The cached ZIP if usable: a user-set value never expires; an auto-detected one honours the TTL. */
export function getGeoZip() {
  const zip = zip5(readLS(ZIP_KEY));
  if (zip.length !== 5) return null;
  if (readLS(SRC_KEY) === 'user') return zip;
  const ts = Number(readLS(TS_KEY) || 0);
  if (ts && Date.now() - ts > TTL_MS) return null;
  return zip;
}

/** Persist a customer-corrected ZIP so other widgets / pages reflect it and detection won't override it. */
export function setGeoZip(value) {
  const zip = zip5(value);
  if (zip.length !== 5) return;
  writeLS(ZIP_KEY, zip);
  writeLS(SRC_KEY, 'user');
  writeLS(TS_KEY, String(Date.now()));
}

/**
 * True while detection can still produce a ZIP: nothing usable is cached,
 * detection is enabled, and this visit has not attempted the API yet.
 * Consumers hold their fallback ZIP back while this is true.
 * @param {{enabled: boolean, apiKey: string}} config
 */
export function canDetectZip(config) {
  if (getGeoZip()) return false;
  if (!config || !config.enabled || !config.apiKey) return false;
  return !wasAttempted();
}

/**
 * Resolve the visitor ZIP: the cached value if present, otherwise a single
 * (deduped) API call. Returns the 5-digit US ZIP or null, and dispatches
 * `geo-zip` on `document` when a fresh value is detected.
 * @param {{enabled: boolean, apiKey: string}} config
 * @returns {Promise<string|null>}
 */
export async function detectZip(config) {
  const cached = getGeoZip();
  if (cached) return cached;
  // Gate before the in-flight share: a disabled consumer must not ride along.
  if (!canDetectZip(config)) return null;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const url = `${ENDPOINT}?apiKey=${encodeURIComponent(config.apiKey)}&fields=zipcode,country_code2`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
      let data;
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`geolocation: ${response.status}`);
        // The timeout has to outlive the body read — a stalled body hangs here too.
        data = await response.json();
      } finally {
        clearTimeout(timer);
      }
      const zip = zip5(data && data.zipcode);
      // A foreign postal code can also be 5 digits, so only a confirmed US ZIP
      // is usable — an absent country code fails closed.
      const country = String((data && data.country_code2) || '').toUpperCase();
      // No country at all means the response shape changed (plan / API version);
      // surface it instead of silently degrading every visitor to the fallback.
      if (!country) console.warn('geolocation: response carries no country_code2, ZIP rejected');
      if (zip.length !== 5 || country !== 'US') return null;
      writeLS(ZIP_KEY, zip);
      writeLS(SRC_KEY, 'auto');
      writeLS(TS_KEY, String(Date.now()));
      document.dispatchEvent(new CustomEvent('geo-zip', { detail: { zip } }));
      return zip;
    } catch (error) {
      return null;
    } finally {
      if (!getGeoZip()) markAttempted();
      inFlight = null;
    }
  })();

  return inFlight;
}
